/**
 * Home Maintenance plugin — Ducks pattern (actions, reducer, types).
 */

import { Appliance, ServiceRecord, Todo } from '@/infra/types/domain';
import type { ApplianceInput, ResearchResult, ResearchSource, ProposedTask } from '@/infra/api/client';

export type { ServiceRecord };
export type { ApplianceInput };
export type { ResearchResult, ResearchSource, ProposedTask };

/**
 * Per-appliance research view state. `result` is the latest ResearchResult
 * (server truth, replayed whole on every fetch — full-delta convention).
 * `polling` is true while the saga is actively re-fetching a pending/running
 * run; `applying` is true while operator-reviewed tasks are being materialized.
 * `error` carries a client-side dispatch/poll failure (distinct from a failed
 * research run, which is `result.status === 'failed'` with `result.error`).
 */
export interface ApplianceResearchState {
  result: ResearchResult | null;
  polling: boolean;
  applying: boolean;
  error: string | null;
}

export const emptyResearchState: ApplianceResearchState = {
  result: null,
  polling: false,
  applying: false,
  error: null,
};

export interface MaintenanceState {
  appliances: Appliance[];
  serviceRecords: ServiceRecord[];
  // Todos linked to the appliance currently shown on the detail page, keyed
  // by appliance id so a stale fetch for a previous appliance can't bleed into
  // the current view.
  applianceTodos: Record<string, Todo[]>;
  applianceTodosLoading: boolean;
  // Research view state keyed by appliance id so a stale poll for one appliance
  // can't bleed into another's review panel.
  research: Record<string, ApplianceResearchState>;
  loading: boolean;
  error: string | null;
}

export const initialState: MaintenanceState = {
  appliances: [],
  serviceRecords: [],
  applianceTodos: {},
  applianceTodosLoading: false,
  research: {},
  loading: false,
  error: null,
};

// ─── Action Types ──────────────────────────────────────────

const SET_APPLIANCES = 'home-maintenance/setAppliances';
const UPSERT_APPLIANCE = 'home-maintenance/upsertAppliance';
const REMOVE_APPLIANCE = 'home-maintenance/removeAppliance';
const SET_SERVICE_RECORDS = 'home-maintenance/setServiceRecords';
const SET_APPLIANCE_TODOS = 'home-maintenance/setApplianceTodos';
const SET_APPLIANCE_TODOS_LOADING = 'home-maintenance/setApplianceTodosLoading';
const SET_LOADING = 'home-maintenance/setMaintenanceLoading';
const SET_ERROR = 'home-maintenance/setMaintenanceError';
const SET_RESEARCH = 'home-maintenance/setResearch';
const SET_RESEARCH_POLLING = 'home-maintenance/setResearchPolling';
const SET_RESEARCH_APPLYING = 'home-maintenance/setResearchApplying';
const SET_RESEARCH_ERROR = 'home-maintenance/setResearchError';

export const START_RESEARCH = 'home-maintenance/startResearch';
export const FETCH_RESEARCH = 'home-maintenance/fetchResearch';
export const APPLY_RESEARCH = 'home-maintenance/applyResearch';
export const STOP_RESEARCH_POLL = 'home-maintenance/stopResearchPoll';

export const FETCH_APPLIANCES = 'home-maintenance/fetchAppliances';
export const FETCH_SERVICE_RECORDS = 'home-maintenance/fetchServiceRecords';
export const CREATE_APPLIANCE = 'home-maintenance/createAppliance';
export const UPDATE_APPLIANCE = 'home-maintenance/updateAppliance';
export const DELETE_APPLIANCE = 'home-maintenance/deleteAppliance';
export const FETCH_APPLIANCE = 'home-maintenance/fetchAppliance';
export const FETCH_APPLIANCE_TODOS = 'home-maintenance/fetchApplianceTodos';

// ─── Action Creators ───────────────────────────────────────

export function setAppliances(appliances: Appliance[]) {
  return { type: SET_APPLIANCES, payload: appliances } as const;
}

/** Insert-or-replace a single appliance in the list (server-truth replay). */
export function upsertAppliance(appliance: Appliance) {
  return { type: UPSERT_APPLIANCE, payload: appliance } as const;
}

export function removeAppliance(id: string) {
  return { type: REMOVE_APPLIANCE, payload: id } as const;
}

export function setServiceRecords(records: ServiceRecord[]) {
  return { type: SET_SERVICE_RECORDS, payload: records } as const;
}

export function setApplianceTodos(applianceId: string, todos: Todo[]) {
  return { type: SET_APPLIANCE_TODOS, payload: { applianceId, todos } } as const;
}

export function setApplianceTodosLoading(loading: boolean) {
  return { type: SET_APPLIANCE_TODOS_LOADING, payload: loading } as const;
}

export function setMaintenanceLoading(loading: boolean) {
  return { type: SET_LOADING, payload: loading } as const;
}

export function setMaintenanceError(error: string | null) {
  return { type: SET_ERROR, payload: error } as const;
}

export function fetchAppliances() {
  return { type: FETCH_APPLIANCES } as const;
}

export function fetchServiceRecords(applianceId: string) {
  return { type: FETCH_SERVICE_RECORDS, payload: applianceId } as const;
}

export function createAppliance(input: ApplianceInput) {
  return { type: CREATE_APPLIANCE, payload: input } as const;
}

export function updateAppliance(id: string, input: ApplianceInput) {
  return { type: UPDATE_APPLIANCE, payload: { id, input } } as const;
}

export function deleteAppliance(id: string) {
  return { type: DELETE_APPLIANCE, payload: id } as const;
}

export function fetchAppliance(id: string) {
  return { type: FETCH_APPLIANCE, payload: id } as const;
}

export function fetchApplianceTodos(applianceId: string) {
  return { type: FETCH_APPLIANCE_TODOS, payload: applianceId } as const;
}

// ─── Research action creators ──────────────────────────────

/** Replace the research result for an appliance (server-truth, full delta). */
export function setResearch(applianceId: string, result: ResearchResult | null) {
  return { type: SET_RESEARCH, payload: { applianceId, result } } as const;
}

export function setResearchPolling(applianceId: string, polling: boolean) {
  return { type: SET_RESEARCH_POLLING, payload: { applianceId, polling } } as const;
}

export function setResearchApplying(applianceId: string, applying: boolean) {
  return { type: SET_RESEARCH_APPLYING, payload: { applianceId, applying } } as const;
}

export function setResearchError(applianceId: string, error: string | null) {
  return { type: SET_RESEARCH_ERROR, payload: { applianceId, error } } as const;
}

/** Kick off a new research run and begin polling. Handled by the saga. */
export function startResearch(applianceId: string) {
  return { type: START_RESEARCH, payload: applianceId } as const;
}

/** Fetch the latest research run once (also used by the saga's poll loop). */
export function fetchResearch(applianceId: string) {
  return { type: FETCH_RESEARCH, payload: applianceId } as const;
}

/** Apply operator-reviewed tasks as recurring todos. Handled by the saga. */
export function applyResearch(applianceId: string, researchId: string, tasks: ProposedTask[]) {
  return { type: APPLY_RESEARCH, payload: { applianceId, researchId, tasks } } as const;
}

/** Stop an in-flight poll loop (dispatched on detail-page unmount). */
export function stopResearchPoll(applianceId: string) {
  return { type: STOP_RESEARCH_POLL, payload: applianceId } as const;
}

// ─── Action Type Union ─────────────────────────────────────

type MaintenanceAction =
  | ReturnType<typeof setAppliances>
  | ReturnType<typeof upsertAppliance>
  | ReturnType<typeof removeAppliance>
  | ReturnType<typeof setServiceRecords>
  | ReturnType<typeof setApplianceTodos>
  | ReturnType<typeof setApplianceTodosLoading>
  | ReturnType<typeof setMaintenanceLoading>
  | ReturnType<typeof setMaintenanceError>
  | ReturnType<typeof setResearch>
  | ReturnType<typeof setResearchPolling>
  | ReturnType<typeof setResearchApplying>
  | ReturnType<typeof setResearchError>;

// ─── Reducer ───────────────────────────────────────────────

export function maintenanceReducer(
  state: MaintenanceState = initialState,
  action: MaintenanceAction | { type: string; payload?: unknown }
): MaintenanceState {
  switch (action.type) {
    case SET_APPLIANCES: {
      const appliances = (action as ReturnType<typeof setAppliances>).payload;
      return { ...state, appliances };
    }

    case UPSERT_APPLIANCE: {
      const appliance = (action as ReturnType<typeof upsertAppliance>).payload;
      const idx = state.appliances.findIndex((a) => a.id === appliance.id);
      const appliances =
        idx === -1
          ? [...state.appliances, appliance]
          : state.appliances.map((a) => (a.id === appliance.id ? appliance : a));
      return { ...state, appliances };
    }

    case REMOVE_APPLIANCE: {
      const id = (action as ReturnType<typeof removeAppliance>).payload;
      const { [id]: _removed, ...applianceTodos } = state.applianceTodos;
      const { [id]: _removedResearch, ...research } = state.research;
      return {
        ...state,
        appliances: state.appliances.filter((a) => a.id !== id),
        applianceTodos,
        research,
      };
    }

    case SET_SERVICE_RECORDS: {
      const serviceRecords = (action as ReturnType<typeof setServiceRecords>).payload;
      return { ...state, serviceRecords };
    }

    case SET_APPLIANCE_TODOS: {
      const { applianceId, todos } = (action as ReturnType<typeof setApplianceTodos>).payload;
      return {
        ...state,
        applianceTodos: { ...state.applianceTodos, [applianceId]: todos },
      };
    }

    case SET_APPLIANCE_TODOS_LOADING: {
      const applianceTodosLoading = (action as ReturnType<typeof setApplianceTodosLoading>).payload;
      return { ...state, applianceTodosLoading };
    }

    case SET_LOADING: {
      const loading = (action as ReturnType<typeof setMaintenanceLoading>).payload;
      return { ...state, loading };
    }

    case SET_ERROR: {
      const error = (action as ReturnType<typeof setMaintenanceError>).payload;
      return { ...state, error };
    }

    case SET_RESEARCH: {
      const { applianceId, result } = (action as ReturnType<typeof setResearch>).payload;
      const prev = state.research[applianceId] ?? emptyResearchState;
      return {
        ...state,
        research: { ...state.research, [applianceId]: { ...prev, result, error: null } },
      };
    }

    case SET_RESEARCH_POLLING: {
      const { applianceId, polling } = (action as ReturnType<typeof setResearchPolling>).payload;
      const prev = state.research[applianceId] ?? emptyResearchState;
      return {
        ...state,
        research: { ...state.research, [applianceId]: { ...prev, polling } },
      };
    }

    case SET_RESEARCH_APPLYING: {
      const { applianceId, applying } = (action as ReturnType<typeof setResearchApplying>).payload;
      const prev = state.research[applianceId] ?? emptyResearchState;
      return {
        ...state,
        research: { ...state.research, [applianceId]: { ...prev, applying } },
      };
    }

    case SET_RESEARCH_ERROR: {
      const { applianceId, error } = (action as ReturnType<typeof setResearchError>).payload;
      const prev = state.research[applianceId] ?? emptyResearchState;
      return {
        ...state,
        research: { ...state.research, [applianceId]: { ...prev, error } },
      };
    }

    default:
      return state;
  }
}
